/* eslint-disable indent */
import React, { useState, forwardRef, useImperativeHandle } from 'react'
import moment from 'moment'
import { parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import {
  TextField,
  InputLabel,
  Box,
  OutlinedInput,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Chip,
  FormHelperText
} from '@mui/material'
import { DatePicker, DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import {
  weekdaysNames,
  resourcesTypes,
  variantTypes,
  textFieldTypes,
  timeTypes,
  scopeTypes
} from '../../../../constants'
import { FormTitle } from '../../../forms'
import { iconNames } from '../../../icons'
import { ButtonFormula } from '../../../buttons'
import { Logger, LoggerTypes, removeSpace, ucFirst } from '../../../../utils'
import './style.scss'

const DEFAULT_WORKTIME_START = '00:00'
const DEFAULT_WORKTIME_END = '23:59'

const defaultValues = {
  id: null,
  name: '',
  description: '',
  type: 'HUMAN', // resourcesTypes.HUMAN
  quantity: '0',
  worktime_start: DEFAULT_WORKTIME_START,
  worktime_end: DEFAULT_WORKTIME_END,
  non_working_days: ['SATURDAY', 'SUNDAY'],
  non_working_period_start: null,
  non_working_period_end: null
}

export const AttributeResourceForm = forwardRef(
  ({ setOpenDialog, title, resource, parameters, dispatchAction, errors }, ref) => {
    const { t } = useTranslation()

    const timeFormatDisplay = 'HH:mm'
    const periodFormatSave = 'YYYY-MM-DD'
    const periodFormatDisplay = 'MMMM, dd'

    const [id] = useState(resource.id || defaultValues.id)
    const [name, setName] = useState(resource.name || defaultValues.name)
    const [description, setDescription] = useState(resource.description || defaultValues.description)
    const [type, setType] = useState(resource.type || defaultValues.type)
    const [quantity, setQuantity] = useState(resource.quantity || defaultValues.quantity)
    const [worktime_start, setWorkTimeStart] = useState(resource.worktime_start || defaultValues.worktime_start)
    const [worktime_end, setWorkTimeEnd] = useState(resource.worktime_end || defaultValues.worktime_end)
    const [non_working_days, setNonWorkingDays] = useState(resource.non_working_days || defaultValues.non_working_days)
    const [non_working_period_start, setNonWorkingPeriodStart] = useState(
      resource.non_working_period_start || defaultValues.non_working_period_start
    )
    const [non_working_period_end, setNonWorkingPeriodEnd] = useState(
      resource.non_working_period_end || defaultValues.non_working_period_end
    )

    const worktime_start_hours = worktime_start.split(':')[0] || '00'
    const worktime_start_minutes = worktime_start.split(':')[1] || '00'
    const worktime_end_hours = worktime_end.split(':')[0] || '00'
    const worktime_end_minutes = worktime_end.split(':')[1] || '00'

    const onChangeName = (e) => setName(removeSpace(e.target.value))
    const onChangeDescription = (e) => setDescription(e.target.value)
    const onChangeType = (e) => setType(e.target.value)
    const onChangeQuantity = (e) => setQuantity(e.target.value)
    const onChangeWorkTimeStart = (e) => setWorkTimeStart(moment(e).format(timeFormatDisplay))
    const onChangeWorkTimeEnd = (e) => setWorkTimeEnd(moment(e).format(timeFormatDisplay))
    const onChangeNonWorkingPeriodStart = (e) => setNonWorkingPeriodStart(moment(e).format(periodFormatSave))
    const onChangeNonWorkingPeriodEnd = (e) => setNonWorkingPeriodEnd(moment(e).format(periodFormatSave))
    const onQuantityFormulaValidated = (value, equation, formula) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onQuantityFormulaValidated()', {
        value,
        equation,
        formula
      })

      if (value) {
        setQuantity(value)
      }
    }

    const isDisabled = resource?.scope === scopeTypes.IMPORT

    const onCloseDialog = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onCloseDialog()')
      setOpenDialog(false)
    }

    const onChangeNonWorkingDays = (event) => {
      const {
        target: { value }
      } = event

      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onChangeNonWorkingDays()', { value })

      setNonWorkingDays(typeof value === 'string' ? value.split(',') : value)
    }

    // ---

    useImperativeHandle(ref, () => ({
      onSubmitForm() {
        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onSubmitForm() BEFORE', {
          worktime_start,
          len: (worktime_start.match(/:/g) || []).length
        })

        const newResource = {
          name: name === '' ? null : name,
          description: description === '' ? null : description,
          type: type === '' ? null : type,
          quantity: quantity === '' ? null : quantity,
          worktime_start:
            worktime_start === null
              ? null
              : (worktime_start.match(/:/g) || []).length === 1
              ? `${worktime_start}:00`
              : worktime_start,
          worktime_end:
            worktime_end === null
              ? null
              : (worktime_end.match(/:/g) || []).length === 1
              ? `${worktime_end}:00`
              : worktime_end,
          non_working_days: non_working_days && non_working_days.length > 0 ? non_working_days : [],
          non_working_period_start: non_working_period_start === null ? null : non_working_period_start,
          non_working_period_end: non_working_period_end === null ? null : non_working_period_end
        }

        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onSubmitForm()', {
          newResource,
          resource,
          dispatchAction
        })

        const _onSuccess = (res) => {
          Logger.trace(LoggerTypes.CONTROLLER, 'AttributeResourceForm.onSubmitForm()._onSuccess()', { res })

          // -- close popin only in case of success
          onCloseDialog()
        }

        dispatchAction(newResource, id, _onSuccess)
      }
    }))

    Logger.trace(LoggerTypes.CONTAINER, 'AttributeResourceForm.render()', {
      parameters,
      defaultValues,
      resource,
      _resource: {
        id,
        name,
        description,
        type,
        quantity,
        worktime_start,
        worktime_end,
        non_working_days,
        non_working_period_start,
        non_working_period_end
      },
      ref
    })

    return (
      <div className="AttributeForm AttributeResourceForm">
        <FormTitle title={title} icon={iconNames.RESOURCE} iconSize={18} />
        {isDisabled ? <div className="importGlobal">{t('global.title')}</div> : ''}
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2} style={{ float: 'left' }}>
                {/*  name  */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.TEXT}
                    name="name"
                    id="name"
                    className="common_fullwidth"
                    value={name}
                    disabled={isDisabled}
                    variant={variantTypes.OUTLINED}
                    label={t('attributes.resources.name.label')}
                    onChange={onChangeName}
                    placeholder={t('attributes.resources.name.placeholder')}
                    error={Boolean(errors && errors?.name)}
                    helperText={(errors && errors?.name) || ''}
                  />
                </Grid>

                {/* type */}
                <Grid item xs={12} sm={12}>
                  <FormControl error={Boolean(errors && errors?.type)}>
                    <InputLabel>{t('attributes.resources.type.label')}</InputLabel>
                    <Select
                      value={type}
                      defaultValue={resourcesTypes.HUMAN}
                      className="common_fullwidth"
                      onChange={onChangeType}
                      disabled={isDisabled}
                      variant={variantTypes.OUTLINED}
                      label={t('attributes.resources.type.label')}
                    >
                      <MenuItem value={resourcesTypes.HUMAN}>{t('attributes.resources.types.human')}</MenuItem>
                      <MenuItem value={resourcesTypes.MATERIAL}>{t('attributes.resources.types.material')}</MenuItem>
                    </Select>
                    <FormHelperText>{(errors && errors?.type) || ''}</FormHelperText>
                  </FormControl>
                </Grid>

                {/*  quantity  */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.NUMBER}
                    name="quantity"
                    id="quantity"
                    style={{ width: '85%', marginRight: '1.5%' }}
                    value={quantity}
                    variant={variantTypes.OUTLINED}
                    label={t('attributes.resources.quantity.label')}
                    onChange={onChangeQuantity}
                    placeholder={t('attributes.resources.quantity.placeholder')}
                    error={Boolean(errors && errors?.quantity)}
                    helperText={(errors && errors?.quantity) || ''}
                  />
                  <ButtonFormula
                    forwardedRef={ref}
                    parameters={parameters}
                    onValidate={onQuantityFormulaValidated}
                    tooltip={t('attributes.resources.quantity.formula_tooltip')}
                  />
                </Grid>

                {/*  worktime_start  /  worktime_end  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  <FormControl error={Boolean(errors && errors?.worktime_start)} className="time">
                    <LocalizationProvider dateAdapter={AdapterDateFns} className="common_fullwidth">
                      {/* @see https://mui.com/x/api/date-pickers/time-picker/ */}
                      <DesktopTimePicker
                        label={t('attributes.resources.worktime.start')}
                        defaultValue={DEFAULT_WORKTIME_START}
                        value={new Date(2023, 12, 30, worktime_start_hours, worktime_start_minutes)}
                        views={[timeTypes.HOURS, timeTypes.MINUTES]}
                        format={timeFormatDisplay}
                        ampm={false}
                        disabled={isDisabled}
                        variant={variantTypes.OUTLINED}
                        onChange={onChangeWorkTimeStart}
                        renderInput={(params) => <TextField className="time-textfield" {...params} />}
                        // textField={<TextField className="time-textfield" />}
                      />
                    </LocalizationProvider>
                    <FormHelperText>{(errors && errors?.worktime_start) || ''}</FormHelperText>
                  </FormControl>
                  <FormControl error={Boolean(errors && errors?.worktime_end)} className="time">
                    <LocalizationProvider dateAdapter={AdapterDateFns} className="common_fullwidth">
                      {/* @see https://mui.com/x/api/date-pickers/time-picker/ */}
                      <DesktopTimePicker
                        label={t('attributes.resources.worktime.end')}
                        defaultValue={DEFAULT_WORKTIME_END}
                        value={new Date(2023, 12, 30, worktime_end_hours, worktime_end_minutes)}
                        views={[timeTypes.HOURS, timeTypes.MINUTES]}
                        format={timeFormatDisplay}
                        formatDensity="spacious"
                        ampmInClock={false}
                        ampm={false}
                        disabled={isDisabled}
                        variant={variantTypes.OUTLINED}
                        onChange={onChangeWorkTimeEnd}
                        renderInput={(params) => <TextField className="time-textfield" {...params} />}
                        // textField={<TextField className="time-textfield" />}
                      />
                    </LocalizationProvider>
                    <FormHelperText>{(errors && errors?.worktime_end) || ''}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              {/* description */}
              <Grid item sm={12} xs={12} className="description-container">
                <TextField
                  type={textFieldTypes.TEXT}
                  multiline={true}
                  rows={10.4}
                  name="description"
                  id="description"
                  disabled={isDisabled}
                  className="common_fullwidth"
                  value={description}
                  variant={variantTypes.OUTLINED}
                  label={t('attributes.resources.description.label')}
                  onChange={onChangeDescription}
                  placeholder={t('attributes.resources.description.placeholder')}
                  error={Boolean(errors && errors?.description)}
                  helperText={(errors && errors?.description) || ''}
                />
              </Grid>
            </Grid>

            {/*  non_working_days  */}
            <Grid item xs={12} sm={12}>
              <FormControl error={Boolean(errors && errors?.non_working_days)} className="common_fullwidth">
                <InputLabel id="demo-multiple-chip-label">{t('attributes.resources.non_working.days')}</InputLabel>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  className="common_fullwidth"
                  multiple={true}
                  disabled={isDisabled}
                  value={non_working_days}
                  variant={variantTypes.OUTLINED}
                  onChange={onChangeNonWorkingDays}
                  input={<OutlinedInput id="select-multiple-chip" label={t('attributes.resources.non_working.days')} />}
                  renderValue={(selected) => (
                    <Box className="time" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value, index) => (
                        <Chip key={`${value}-${index}`} label={ucFirst(value)} />
                      ))}
                    </Box>
                  )}
                >
                  {Object.keys(weekdaysNames).map((dayName, index) => (
                    <MenuItem key={index} value={dayName}>
                      {t(`common.weekdays.${dayName}`)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{(errors && errors?.non_working_days) || ''}</FormHelperText>
              </FormControl>
            </Grid>

            {/*  non_working_period_start  /  non_working_period_end  */}
            <Grid item xs={12} sm={12}>
              {/*  non_working_period_start  */}
              <FormControl error={Boolean(errors && errors?.non_working_period_start)} className="time">
                <LocalizationProvider dateAdapter={AdapterDateFns} className="common_fullwidth">
                  <DatePicker
                    label={t('attributes.resources.non_working.period_start')}
                    format={periodFormatDisplay}
                    views={['day']}
                    disabled={isDisabled}
                    variant={variantTypes.OUTLINED}
                    value={non_working_period_start === null ? null : parseISO(non_working_period_start)}
                    onChange={onChangeNonWorkingPeriodStart}
                    renderInput={(params) => <TextField className="time-textfield" {...params} />}
                  />
                </LocalizationProvider>
                <FormHelperText>{(errors && errors?.non_working_period_start) || ''}</FormHelperText>
              </FormControl>

              {/*  non_working_period_end  */}
              <FormControl error={Boolean(errors && errors?.non_working_period_end)} className="time">
                <LocalizationProvider dateAdapter={AdapterDateFns} className="common_fullwidth">
                  <DatePicker
                    label={t('attributes.resources.non_working.period_end')}
                    format={periodFormatDisplay}
                    views={['day']}
                    disabled={isDisabled}
                    value={non_working_period_end === null ? null : parseISO(non_working_period_end)}
                    variant={variantTypes.OUTLINED}
                    onChange={onChangeNonWorkingPeriodEnd}
                    renderInput={(params) => <TextField className="time-textfield" {...params} />}
                  />
                </LocalizationProvider>
                <FormHelperText>{(errors && errors?.non_working_period_end) || ''}</FormHelperText>
              </FormControl>
            </Grid>
            {errors && errors?.generic ? (
              <div className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained errorField">
                {errors.generic}
              </div>
            ) : null}
          </Grid>
        </form>
      </div>
    )
  }
)

AttributeResourceForm.displayName = 'AttributeResourceForm'

AttributeResourceForm.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  parameters: PropTypes.arrayOf(PropTypes.object),
  resource: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.oneOf(['HUMAN', 'MATERIAL', undefined]),
    quantity: PropTypes.string,
    worktime_start: PropTypes.string,
    worktime_end: PropTypes.string,
    non_working_days: PropTypes.array,
    non_working_period_start: PropTypes.string,
    non_working_period_end: PropTypes.string,
    scope: PropTypes.string
  }),
  errors: PropTypes.shape({
    name: PropTypes.any,
    description: PropTypes.any,
    type: PropTypes.any,
    quantity: PropTypes.any,
    worktime_start: PropTypes.any,
    worktime_end: PropTypes.any,
    non_working_days: PropTypes.any,
    non_working_period_start: PropTypes.any,
    non_working_period_end: PropTypes.any,
    generic: PropTypes.any
  })
}

AttributeResourceForm.defaultProps = {
  setOpenDialog: () => {},
  dispatchAction: () => {},
  title: null,
  parameters: [],
  resource: {
    id: defaultValues.id,
    name: defaultValues.name,
    description: defaultValues.description,
    type: defaultValues.type,
    quantity: defaultValues.quantity,
    worktime_start: defaultValues.worktime_start,
    worktime_end: defaultValues.worktime_end,
    non_working_days: defaultValues.non_working_days,
    non_working_period_start: defaultValues.non_working_period_start,
    non_working_period_end: defaultValues.non_working_period_end
  },
  errors: null
}
